import type { DateTime } from "luxon";
import type { UserOrEmailMapping } from "../../Admin/typings/admin.types";
import type {
  BookingType,
  NotSerializedBookingService,
  VisitorRegistrationEntry
} from "../../Booking-Form/typings/booking-inputs";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { OnlineMeetingProvider } from "../../../components/Settings/typings/calendarSync-integration.type";

/**
 * Open payload for the Dashboard2 colleague workplace floor dialog (#1572).
 * Date is a calendar day. A workplace, parking, charging, or conference booking can choose the floor.
 */
export type ColleagueWorkplaceFloorOpenArgs = {
  /** Calendar day to load (user-local `yyyy-MM-dd`). */
  date: string;
  /** Booked workplace, parking, charging, or conference inventory to mark on the plan. */
  highlightInventoryId?: number;
  /** Physical location to load; favorite site, or the site of the booking that opened the plan. */
  locationInventoryId?: number;
  /** Display name for the location control above the floor chips. */
  locationName?: string;
  /**
   * Workplace, parking, and charging inventories booked by the logged-in user that day.
   * Paints initials or a photo on those objects. Does not choose the opened floor.
   */
  occupantInventoryIds?: number[];
  /**
   * That person's bookings on those objects, with each booking's own start and end.
   * Desk hover uses these hours. It does not use the floor's Von and Bis.
   */
  ownOccupancyBookings?: ColleagueOwnOccupancyBooking[];
  /**
   * Which floors to load. Workplace unless the plan was opened from a parking, charging,
   * or conference booking. A workplace request omits the other types.
   */
  availabilityBookingType?: BookingType;
  /**
   * Floor id of the parking, charging, or conference booking.
   * Used only when no highlight is set. A leftover highlight does not reuse this id.
   */
  preferredFloorId?: number;
  /**
   * Floor name from the booking that opened the plan.
   * Not used to choose a floor: the same name can belong to a different floor.
   */
  preferredFloorName?: string;
};

/**
 * Dialog session state after open — date/time drive availability refetch.
 */
export type ColleagueWorkplaceFloorSession = ColleagueWorkplaceFloorOpenArgs & {
  open: boolean;
  startTime: string;
  endTime: string;
};

/**
 * One of the logged-in user's bookings already loaded for the open day.
 * Hover prints `start`–`end`, not the floor time window.
 */
export type ColleagueOwnOccupancyBooking = {
  /** Desk, bay, or charger this booking occupies. */
  bookingInventoryId: number;
  /** Schedule row id when the day has more than one booking on that object. */
  bookingId?: number;
  /** Booking start from the schedule (ISO). */
  start: string;
  /** Booking end from the schedule (ISO). */
  end: string;
};

/** Floor chip shown above the Pixi canvas. */
export type ColleagueFloorChip = {
  id: number;
  name: string;
  colleagueCount: number;
};

export type ColleagueSearchOption = ConnectedBookingData;

export type ColleagueWorkplaceFloorTimeContext = {
  date: DateTime;
  startTime: string;
  endTime: string;
};

/**
 * Fields the colleague-floor conference sheet sends when the person books a room.
 * Services and the Teams switch are the same values the conference form already stores.
 */
export type ColleagueFloorConferenceDraft = {
  /** Conference zone inventory that was clicked. */
  inventoryId: number;
  /** Meeting title. Empty titles are not submitted. */
  subject: string;
  /** Optional note on the calendar invite. */
  description: string;
  /** Meeting participants (calendar invite), not reception visitors. */
  guests: UserOrEmailMapping[];
  /** Reception visitors. Empty when the tenant has no Visitor Management license. */
  visitorRegistrations: VisitorRegistrationEntry[];
  /** Teams link when this organisation adds one. Null when the switch is off or absent. */
  onlineMeetingProvider: OnlineMeetingProvider | null;
  /** Catering, equipment, and room layout chosen in the existing services editor. */
  conferenceService: NotSerializedBookingService | undefined;
};

/**
 * Why Book stays disabled on the conference sheet.
 * Loading is checked before a missing title or a required service.
 */
export type ColleagueFloorConferenceBookBlock = "loading" | "title" | "service";

/**
 * What a workplace click does on the colleague floor canvas (#1572 v0.2).
 */
export enum ColleagueDeskClickKind {
  /** Free desk: nested book confirm. */
  BOOK = "book",
  /** Occupied or partial: calendar booking(s) for that desk. */
  OCCUPANCY = "occupancy",
  /** Not interactive (forbidden, disabled, missing id). */
  NONE = "none"
}
